// BonBoZ: PING KHÁCH MỚI NHẮN — thẻ nổi góc trên phải khi khách gửi tin.
//
// Chatwoot chỉ báo tin mới bằng tiếng "ding" + chấm đỏ favicon: nhân viên biết
// CÓ tin nhưng không biết của AI, phải quét cả danh sách mới thấy. Thẻ này trả
// lời luôn "ai, trang nào, nói gì" và bấm một cái là nhảy tới.
//
// Module reactive nhỏ thay vì store Vuex mới — cùng kiểu `helper/pageColor.js`.
// Không có store nào cần state này ngoài đúng một component hiển thị.
//
// Âm thanh KHÔNG đụng tới: `DashboardAudioNotificationHelper` vẫn chạy y như cũ,
// module này chỉ ĐỌC lại cấu hình của nó để hai bên lọc giống nhau.
import { ref } from 'vue';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import audioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioAlerts/AudioMessageHelper';

export const PING_TTL_MS = 7000;
export const MAX_VISIBLE_PINGS = 3;
const TICK_MS = 100;

/** Thẻ đang sống, mới nhất giữ nguyên vị trí cũ nếu cùng hội thoại. */
export const pings = ref([]);
/** Nhịp đồng hồ dùng chung để vẽ vạch đếm ngược; không có nó vạch đứng im. */
export const clockTick = ref(Date.now());

// BonBoZ: BẪY `DEFAULT_ALERT_TYPE = ['none']`.
// Người dùng chưa bao giờ mở Cài đặt cá nhân thì `audioAlertType` là `['none']`,
// và `shouldNotifyOnMessage` của helper âm thanh trả `false` cho mọi tin. Dùng
// thẳng hàm đó thì tính năng chết ngay hôm ra mắt: không ai nhận được ping nào.
// Nên: `none`/rỗng -> mặc định "giao cho tôi + chưa ai nhận". Ai đã chọn rõ một
// loại thì tôn trọng đúng lựa chọn của họ, kể cả `notme` hay `all`.
const FALLBACK_ALERT_TYPES = [EVENT_TYPES.ASSIGNED, EVENT_TYPES.UNASSIGNED];

export const matchesAlertType = (audioAlertType, message, currentUserId) => {
  const chosen = (audioAlertType || []).filter(t => t && t !== 'none');
  const effective = chosen.length ? chosen : FALLBACK_ALERT_TYPES;
  if (effective.includes('all')) return true;

  const assignedToMe = isConversationAssignedToMe(message, currentUserId);
  const unassigned = isConversationUnassigned(message);

  return effective.some(type => {
    if (type === EVENT_TYPES.ASSIGNED || type === 'mine') return assignedToMe;
    if (type === EVENT_TYPES.UNASSIGNED) return unassigned;
    if (type === EVENT_TYPES.NOTME) return !unassigned && !assignedToMe;
    return false;
  });
};

// Cùng bộ luật với `DashboardAudioNotificationHelper.onNewMessage`, TRỪ cổng
// `playAlertOnlyWhenHidden`: cổng đó mặc định bật và sẽ chặn hết ping khi người
// dùng đang nhìn dashboard — đúng lúc thẻ có ích nhất. Đặc tả chốt là thẻ hiện
// cả khi tab đang mở lẫn khi bị ẩn.
export const shouldPingOnMessage = (
  message,
  helper = audioNotificationHelper
) => {
  const currentUser = helper.currentUser;
  if (!currentUser) return false;
  if (!helper.store.hasConversationPermission(currentUser)) return false;
  if (helper.store.isMessageFromPendingConversation(message)) return false;
  if (isMessageFromCurrentUser(message, currentUser.id)) return false;
  // Chỉ tin của khách. Ghi chú riêng của đồng nghiệp không phải "khách mới nhắn".
  if (message?.message_type !== MESSAGE_TYPE.INCOMING) return false;
  if (message?.private) return false;
  // Đang mở đúng hội thoại đó thì tin đã nằm ngay trước mắt rồi.
  if (helper.store.isMessageFromCurrentConversation(message)) return false;

  return matchesAlertType(
    helper.notificationConfig.audioAlertType,
    message,
    currentUser.id
  );
};

/** Thẻ đang bị rê chuột lên thì đồng hồ dừng, không bị dọn. */
export const reapPings = (now = Date.now()) => {
  pings.value = pings.value.filter(p => p.pausedAt || p.expiresAt > now);
};

let tickTimer = null;

const stopTicker = () => {
  if (tickTimer) clearInterval(tickTimer);
  tickTimer = null;
};

// Đồng hồ chạy kể cả khi tab bị ẩn — chốt là thẻ hết hạn dù không ai nhìn, nên
// KHÔNG bám `visibilitychange`. Trình duyệt bóp interval của tab ẩn xuống ~1s,
// vạch vẽ thưa hơn nhưng `expiresAt` là mốc thời gian thật nên vẫn hết đúng hạn.
const startTicker = () => {
  if (tickTimer) return;
  tickTimer = setInterval(() => {
    clockTick.value = Date.now();
    reapPings(clockTick.value);
    if (!pings.value.length) stopTicker();
  }, TICK_MS);
};

export const addPing = (entry, now = Date.now()) => {
  const ping = { ...entry, expiresAt: now + PING_TTL_MS, pausedAt: 0 };
  const next = pings.value.slice();
  // Một hội thoại một thẻ: tin mới thay nội dung thẻ cũ TẠI CHỖ và đặt lại đồng
  // hồ, không đẩy lên đầu (thẻ nhảy chỗ dưới con trỏ chuột là cách tốt nhất để
  // nhân viên bấm nhầm sang hội thoại khác).
  const index = next.findIndex(p => p.conversationId === entry.conversationId);
  if (index === -1) next.unshift(ping);
  else next[index] = ping;
  pings.value = next;
  startTicker();
  return ping;
};

export const pausePing = (ping, now = Date.now()) => {
  if (ping && !ping.pausedAt) ping.pausedAt = now;
};

export const resumePing = (ping, now = Date.now()) => {
  if (!ping?.pausedAt) return;
  ping.expiresAt += now - ping.pausedAt;
  ping.pausedAt = 0;
};

export const dismissPing = conversationId => {
  pings.value = pings.value.filter(p => p.conversationId !== conversationId);
  if (!pings.value.length) stopTicker();
};

// BonBoZ: THÔNG BÁO CỦA TRÌNH DUYỆT — thẻ nổi chỉ thấy được khi đang nhìn
// dashboard, mà lúc cần báo nhất là lúc nhân viên đã sang tab hoặc app khác.
//
// Vị trí thông báo do Windows quyết (góc dưới phải), app không chỉnh được.

/** Cắm vào `actionCable.onMessageCreated`. Payload là `Message#push_event_data`. */
//
// Chỉ dựng thẻ nổi TRONG trang, không tạo thông báo hệ thống. Thông báo hệ
// thống (banner, tiếng kêu ngoài trang) chỉ còn MỘT nguồn: thông báo đẩy của
// server qua `sw.js`. Trước đây trang cũng tự tạo một cái khi tab bị ẩn, nên
// laptop vừa để tab mở vừa bật push thì một câu khách nhắn kêu HAI lần — mỗi
// đường một luật lọc, một màn cài đặt, không gộp được với nhau.
export const pingOnMessageCreated = message => {
  if (!shouldPingOnMessage(message)) return;
  addPing({
    conversationId: message.conversation_id,
    inboxId: message.inbox_id,
    senderName: message.sender?.name || '',
    senderAvatar: message.sender?.thumbnail || '',
    content: message.content || '',
    createdAt: message.created_at,
  });
};
